import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type TrailDetailsActionLocation from '@alltrails/amplitude/enums/TrailDetailsActionLocation';
import type TrailDetailsShareOption from '@alltrails/amplitude/enums/TrailDetailsShareOption';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsShareThisTrailOptionClicked from '@alltrails/amplitude/events/logTrailDetailsShareThisTrailOptionClicked';
import Button from '@alltrails/denali/components/Button';
import IconButton from '@alltrails/denali/components/IconButton';
import Share from '@alltrails/denali/icons/Share';
import type { Size } from '@alltrails/denali/types';
import type TrailDetails from '@alltrails/types/TrailDetails';
import getTrailShareObject from '../../utils/getTrailShareObject';

const ShareModal = dynamic(() => import('@alltrails/sharing/components/ShareModal/ShareModal'), { ssr: false });

type ShareCtaProps = {
  buttonLocation?: TrailDetailsActionLocation;
  displayAsButton?: boolean;
  isExplorePage?: boolean;
  trailDetails: TrailDetails;
  size?: Size<'sm' | 'md'>;
};

const ShareCta = ({ buttonLocation, displayAsButton, isExplorePage, trailDetails, size = 'md' }: ShareCtaProps) => {
  const intl = useIntl();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const shareObject = useMemo(() => {
    const baseShareObject = getTrailShareObject(trailDetails);
    if (isExplorePage) {
      return {
        ...baseShareObject,
        widgetSlug: baseShareObject.slug,
        slug: `explore/${baseShareObject.slug}`
      };
    }
    return baseShareObject;
  }, [isExplorePage, trailDetails]);

  const onShareClick = useCallback(() => {
    setIsShareModalOpen(true);
    logTrailDetailsActionTapped({
      trail_id: trailDetails.id.toString(),
      trail_page_action: 'share',
      trail_page_location: buttonLocation
    });
  }, [buttonLocation, trailDetails.id]);

  const onShareOptionClick = useCallback(
    (shareOption: TrailDetailsShareOption) => {
      logTrailDetailsShareThisTrailOptionClicked({ share_option_clicked: shareOption, trail_id: trailDetails.id.toString() });
    },
    [trailDetails.id]
  );

  return (
    <>
      {displayAsButton ? (
        <Button icon={{ Component: Share }} onClick={onShareClick} size={size} text={intl.formatMessage({ defaultMessage: 'Share' })} variant="default" />
      ) : (
        <IconButton icon={{ Component: Share }} onClick={onShareClick} size={size} title={intl.formatMessage({ defaultMessage: 'Share' })} />
      )}
      {isShareModalOpen && (
        <ShareModal isOpen onClose={() => setIsShareModalOpen(false)} onShareOptionClick={onShareOptionClick} shareObject={shareObject} />
      )}
    </>
  );
};

export default ShareCta;
